import React, { useEffect, useContext } from 'react';
import { FilterCategory, FilterOptions, Filters, filterOptionToLabelMap } from './ContentFilters';
import {
  updateFilterOptionWithValue,
  getPublicationInitialFilterOptions,
  applyFilterToPublication,
  isBRApproved,
} from '../utils/ContentFilterUtils';
import { BR_APPROVED_VALUE, barRaiserReviewStatusDisplayNames } from '../utils/DisplayNames';
import constants from '../../common/constants';
import NewContentFilters from './NewContentFilters';
import { Publication } from '@amzn/awscat-pattern-library-service-model';
import { HomePageCtx } from './content-home-page';
import { getFiltersToApply } from '../utils/ContentFilterUtils';
import { Box } from '@amzn/awsui-components-core';
import { SelectedPublicationFilters } from './content-home-page';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';

interface Props {
  publications: Publication[];
}

/**
 * Carries the user's picked options and open sections over to filters rebuilt from new content,
 * so picks survive while content keeps loading; a picked option the new content lacks stays with a zero count.
 */
export function keepFilterSelections(next: Filters, previous: Filters): Filters {
  const merged: Filters = {};
  Object.entries(next).forEach(([category, nextFilter]) => {
    const previousFilter = previous[category];
    if (!previousFilter) {
      merged[category] = nextFilter;
      return;
    }

    const options: FilterOptions = { ...nextFilter.options };
    Object.entries(previousFilter.options)
      .filter(([, option]) => option.selected)
      .forEach(([key, option]) => {
        options[key] = { ...(nextFilter.options[key] ?? { ...option, count: 0 }), selected: true };
      });
    merged[category] = { ...nextFilter, expanded: previousFilter.expanded, options };
  });
  return merged;
}

const NewPublicationFilters = (props: Props) => {
  const { publications } = props;
  const { filterOptions, setFilterOptions, setFilteredPublications } = useContext(HomePageCtx);
  const isEarlySharingEnabled = useFeatureFlag('EarlySharing');
  const updateFilterOptions = (initialFilterOptions: Filters) => {
    publications.forEach((item) => {
      Object.values(initialFilterOptions).forEach((filter) => {
        if (filter && Array.isArray(filter.values)) {
          const filterValuesSet = new Set<string>();
          filter.values.forEach((v) => {
            const filterValue =
              filter.metadata !== ''
                ? // @ts-expect-error TS7053 - strict migration
                  item[filter.metadata][v]
                : filter.mappedValues
                  ? // @ts-expect-error TS7053 - strict migration
                    filter.mappedValues.get(item[v])
                  : // @ts-expect-error TS7053 - strict migration
                    item[v];
            updateFilterOptionWithValue(filterValue, filter, filterValuesSet);
          });
        }
      });
    });
    const hasIncludeCodeFilter = publications.some(
      (item) => item?.contentTypeMetadata?.codeLinks?.length,
    );
    if (hasIncludeCodeFilter) {
      const contentWithCodeCount = publications.filter(
        (item) => item?.contentTypeMetadata?.codeLinks?.length,
      ).length;
      // @ts-expect-error TS18048 - strict migration
      initialFilterOptions.code.options.includeCode = {
        count: contentWithCodeCount,
        // @ts-expect-error TS2322 - strict migration
        label: filterOptionToLabelMap.get(FilterCategory.IncludeCode),
        selected: false,
        value: FilterCategory.IncludeCode,
      };
    }
    if (isEarlySharingEnabled && initialFilterOptions.barRaiserReviewStatus) {
      const approvedCount = publications.filter(isBRApproved).length;
      initialFilterOptions.barRaiserReviewStatus.options[BR_APPROVED_VALUE] = {
        count: approvedCount,
        // @ts-expect-error TS2322 - strict migration
        label: barRaiserReviewStatusDisplayNames.get(BR_APPROVED_VALUE),
        selected: false,
        value: BR_APPROVED_VALUE,
      };
    }
    return initialFilterOptions;
  };

  const getInitialFilterOptions = () => {
    const hideFilterCategory: FilterCategory[] = [
      FilterCategory.ContentType,
      FilterCategory.PublicationState,
      FilterCategory.Environment,
    ];
    if (!isEarlySharingEnabled) {
      hideFilterCategory.push(FilterCategory.BarRaiserReviewStatus);
    }
    const initialFilterOptions = getPublicationInitialFilterOptions(
      hideFilterCategory,
      isEarlySharingEnabled,
    );
    return updateFilterOptions(initialFilterOptions);
  };

  const applyFilter = (filterOptionsToApply: Filters) => {
    const filtersToApply = getFiltersToApply(filterOptionsToApply);
    const newFilteredPublications = filtersToApply.length
      ? publications.filter((publication) => applyFilterToPublication(publication, filtersToApply))
      : publications;
    setFilteredPublications(newFilteredPublications);
  };

  useEffect(() => {
    setFilterOptions(getInitialFilterOptions());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When more content arrives, then rebuild the options from it and keep what the user already picked.
  useEffect(() => {
    setFilterOptions((previous) => keepFilterSelections(getInitialFilterOptions(), previous));
  }, [props.publications]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    applyFilter(filterOptions);
  }, [filterOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <Box variant="h3">{constants.FILTERS}</Box>
      <SelectedPublicationFilters />
      <Box padding={{ vertical: 's' }}>
        <div className="grey-border" />
      </Box>
      <NewContentFilters />
    </Box>
  );
};

export default NewPublicationFilters;
